import { useCallback, useMemo, useRef, useState } from "react"
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from "react"

import { useViewportBelow } from "@/hooks/use-viewport-below"
import { cn } from "@/lib/utils"
import {
  type ContentSidebarHeaderContext,
  useContentSidebarStore,
} from "@/stores/content-sidebar-store"

import { ContentSidebarConfirmDialog } from "./content-sidebar-confirm-dialog"
import { ContentSidebarDivider } from "./content-sidebar-divider"
import { ContentSidebarHeader } from "./content-sidebar-header"
import {
  getDesktopContentSidebarTracks,
  isDesktopSplitContentSidebarLayout,
  isOverlayContentSidebarLayout,
} from "./content-sidebar-layout-utils"

type ContentSidebarProps = {
  children: ReactNode
  header?: ReactNode
  className?: string
}

const COMPACT_LAYOUT_BREAKPOINT = 1024
const MIN_MAIN_CONTENT_WIDTH_DESKTOP = 500

export function ContentSidebar({ children, header, className }: ContentSidebarProps) {
  const isOpen = useContentSidebarStore((state) => state.isOpen)
  const isFocused = useContentSidebarStore((state) => state.isFocused)
  const panelRatio = useContentSidebarStore((state) => state.panelRatio)
  const currentPanel = useContentSidebarStore((state) => state.currentPanel)
  const closePanel = useContentSidebarStore((state) => state.closePanel)
  const toggleFocus = useContentSidebarStore((state) => state.toggleFocus)
  const setPanelRatio = useContentSidebarStore((state) => state.setPanelRatio)

  const isCompactLayout = useViewportBelow({ breakpoint: COMPACT_LAYOUT_BREAKPOINT })
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isResizing, setIsResizing] = useState(false)

  const hasPanel = isOpen && currentPanel !== null
  const isFocusable = currentPanel?.focusable ?? false
  const isResizable = currentPanel?.resizable ?? true
  const closeable = currentPanel?.closeable ?? true
  const headerMode = currentPanel?.headerMode ?? "default"

  // Canvas panels on compact viewports behave like an off-screen layer when not focused,
  // so main content (chat) stays visible. Focusing canvas (via "Open canvas" badge) makes
  // it full-screen. All other panel types keep the existing compact overlay behavior.
  const isCanvasPanel = currentPanel?.id === "canvas"
  const isCompactOverlay = isCompactLayout && (!isCanvasPanel || isFocused)

  const shouldShowDesktopSplit = isDesktopSplitContentSidebarLayout({
    hasPanel,
    isFocused,
    isCompactLayout,
  })
  const shouldHideMainContent = isOverlayContentSidebarLayout({
    hasPanel,
    isFocused,
    isCompactLayout: isCompactOverlay,
  })
  const { mainTrack: desktopMainTrack, sidebarTrack: desktopSidebarTrack } = useMemo(
    () =>
      getDesktopContentSidebarTracks({
        desktopWidthPx: currentPanel?.desktopWidthPx,
        minMainContentWidth: MIN_MAIN_CONTENT_WIDTH_DESKTOP,
        panelRatio,
      }),
    [currentPanel?.desktopWidthPx, panelRatio]
  )

  const updateRatioFromClientX = useCallback(
    (clientX: number) => {
      if (!containerRef.current) {
        return
      }

      const rect = containerRef.current.getBoundingClientRect()
      if (rect.width <= 0) {
        return
      }

      const nextRatio = (clientX - rect.left) / rect.width
      setPanelRatio(nextRatio)
    },
    [setPanelRatio]
  )

  const endResize = useCallback(() => {
    setIsResizing(false)
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
  }, [])

  const handleDividerMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!shouldShowDesktopSplit || !isResizable) {
        return
      }

      event.preventDefault()
      setIsResizing(true)
      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
      updateRatioFromClientX(event.clientX)

      const handleMouseMove = (moveEvent: MouseEvent) => {
        updateRatioFromClientX(moveEvent.clientX)
      }

      const handleMouseUp = () => {
        window.removeEventListener("mousemove", handleMouseMove)
        window.removeEventListener("mouseup", handleMouseUp)
        endResize()
      }

      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    },
    [endResize, isResizable, shouldShowDesktopSplit, updateRatioFromClientX]
  )

  const sidebarOverlayClassName = useMemo(() => {
    if (!hasPanel) {
      return "pointer-events-none border-l-0"
    }
    if (isFocused || isCompactOverlay) {
      return "left-0 w-full border-l-0"
    }
    if (shouldShowDesktopSplit) {
      return "border-l"
    }
    return "pointer-events-none border-l-0"
  }, [hasPanel, isCompactOverlay, isFocused, shouldShowDesktopSplit])

  const sidebarOverlayStyle = useMemo<CSSProperties | undefined>(() => {
    if (!hasPanel) {
      return {
        left: "100%",
        width: "0%",
      }
    }
    if (isFocused || isCompactOverlay) {
      return undefined
    }
    if (shouldShowDesktopSplit) {
      return {
        left: desktopMainTrack,
        width: desktopSidebarTrack,
      }
    }
    return {
      left: "100%",
      width: "0%",
    }
  }, [
    desktopMainTrack,
    desktopSidebarTrack,
    hasPanel,
    isCompactOverlay,
    isFocused,
    shouldShowDesktopSplit,
  ])

  const headerContext = useMemo<ContentSidebarHeaderContext>(
    () => ({
      title: currentPanel?.title,
      actions: currentPanel?.headerActions,
      isFocused,
      isFocusable,
      closeable,
      onToggleFocus: toggleFocus,
      onClose: closePanel,
    }),
    [
      closePanel,
      closeable,
      currentPanel?.headerActions,
      currentPanel?.title,
      isFocusable,
      isFocused,
      toggleFocus,
    ]
  )

  const panelHeader = useMemo(() => {
    if (!currentPanel || headerMode === "none") {
      return null
    }
    if (headerMode === "custom" && currentPanel.renderHeader) {
      return currentPanel.renderHeader(headerContext)
    }
    return (
      <ContentSidebarHeader
        actions={headerContext.actions}
        closeable={headerContext.closeable}
        isFocusable={headerContext.isFocusable}
        isFocused={headerContext.isFocused}
        title={headerContext.title}
        onClose={headerContext.onClose}
        onToggleFocus={headerContext.onToggleFocus}
      />
    )
  }, [currentPanel, headerContext, headerMode])

  return (
    <div ref={containerRef} className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        style={shouldShowDesktopSplit ? { width: desktopMainTrack } : undefined}
        className={cn(
          "relative flex min-h-0 flex-1 flex-col",
          !isResizing && "transition-[width,opacity] duration-250 ease-out",
          shouldHideMainContent ? "pointer-events-none w-0 opacity-0" : "w-full opacity-100",
          className
        )}
      >
        {header ? <div className="absolute top-0 right-0 left-0 z-50">{header}</div> : null}
        {children}
      </div>

      <div
        style={sidebarOverlayStyle}
        className={cn(
          "bg-background absolute top-0 right-0 bottom-0 z-60 flex flex-col overflow-hidden",
          !isResizing && "transition-[left,width] duration-250 ease-out",
          sidebarOverlayClassName
        )}
      >
        {currentPanel ? (
          <>
            {panelHeader}
            <div className="min-h-0 flex-1 overflow-auto">{currentPanel.content}</div>
          </>
        ) : null}
      </div>

      {shouldShowDesktopSplit && isResizable ? (
        <ContentSidebarDivider leftTrack={desktopMainTrack} onMouseDown={handleDividerMouseDown} />
      ) : null}

      <ContentSidebarConfirmDialog />
    </div>
  )
}
